import React, { memo, useCallback, useMemo, useState, Fragment } from 'react';
import { ContactActivityRequest, ContactActivityType, ContactId } from '@business/entities/contact';
import { OrgId, OrgResponse } from '@business/entities/organization';
import { openAddTicketModal } from '@modules/support/AddTicketModal';
import { Card, CardBody, Collapse, Button, Dropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import { Observable, of } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';
import { SendEmail } from '../TabEmail/SendEmail';
import { NewNote } from './NewNote';
import { NewTask } from './NewTask';
import { useAccountSettings, useSettings } from '@core/utils/hooks/misc/useSettings';
import { openAddCalendarOverlay } from '@modules/CalendarPage/AddCalendarEventOverlay';
import { FormSelectItem } from '@core/components/form';
import { openSelectProposalModal } from '@modules/proposal/SelectProposalModal';
import { ProposalId } from '@business/entities/proposal';
import { EventMeta } from '@business/entities/calendar';
import { openAddProposalModal } from '@modules/proposals/AddProposalModal';
import { CommonService, FileService } from '@business/services';
import { Config } from '@config';
import { useHistory } from '@core/router';
import { openAddPresentationModal } from '@modules/form/AddPresentationModal';
import { FormTypes } from '@business/entities/form';
import { FileResponse } from '@business/entities/file';
import { useObservable } from '@core/utils/hooks/rxjs/useObservable';
import { Session } from '@modules/auth/session/session';

export enum NotesView {
  none,
  note,
  interview,
  interviewInPerson,
  metting,
  other,
  sendEmail,
  appointment,
  task,
}
export interface TabNotesProps {
  contactId?: ContactId;
  orgId?: OrgId;
  organization?: OrgResponse;
  onAdd(activity: ContactActivityRequest): Observable<any>;
  defaultEmail?: string;
  availableEmails?: FormSelectItem<string>[];
  onReload?(): void;
  linkProposal?: boolean;
  selectedProposalIds?: ProposalId[];
  onProposalAdd?(proposalId: ProposalId): void;
  meta?: EventMeta;
  salerep?: string;
  onFormSelect?(item: FileResponse): void;
  hideAppointments?: boolean;
}

export const TabNotes = memo((props: TabNotesProps) => {
  const { onAdd, contactId, organization, linkProposal, selectedProposalIds, onProposalAdd, salerep, onFormSelect, hideAppointments = false } = props;
  const [currentView, setCurrentView] = useState<NotesView>(NotesView.none);
  const accountSettings = useAccountSettings();
  const [showMenu, toggleMenu] = useState(false);
  const [showProposal, toggleProposal] = useState(false);
  const [showPresentation, togglePresentation] = useState(false);
  const user = useObservable(Session.user$);

  const onNewItem = useCallback(
    (req: ContactActivityRequest) => {
      return onAdd(req).pipe(tap(() => setCurrentView(NotesView.none)));
    },
    [onAdd],
  );

  const defaultContact = useMemo(() => {
    if (organization && organization.contacts.length > 0) {
      return organization.contacts[0].id;
    } else if (contactId) {
      return contactId;
    }
    return '0';
  }, [organization, contactId]);

  const onChangeView = useCallback(
    (view: NotesView) => {
      setCurrentView(NotesView.none);

      if (currentView !== view) {
        setTimeout(() => setCurrentView(view), 100);
        // setCurrentView(view);
        // setCurrentView(NotesView.none);
      } else {
      }
    },
    [currentView],
  );

  const settings = useSettings();
  const isOnePageProposalEnabled = useMemo(() => CommonService.isOnePageProposalEnabled(settings), [settings]);
  const history = useHistory();
  const canAddPresentation = useMemo(() => props.meta && FileService.canAddPresentation(props.meta.type as FormTypes, user), [props.meta, user]);

  const content = (
    <div className="btn-group-toggle" data-toggle="buttons">
      <Dropdown
        isOpen={showMenu}
        toggle={() => toggleMenu(!showMenu)}
        className="btn-group"
        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
      >
        <DropdownToggle className="btn btn-primary waves-light waves-effect dropdown-toggle mx-1 mt-2" tag="button">
          <i className="bx bxs-phone mr-2"></i>
          Call
          <i className="mdi mdi-chevron-down ml-2" />
        </DropdownToggle>
        <DropdownMenu>
          <a
            className="dropdown-item"
            onClick={() => {
              onNewItem({ notesResult: 'Call - Left Message', contact: defaultContact, activityType: ContactActivityType.leftMessage }).subscribe();
              toggleMenu(false);
            }}
          >
            <i className="bx bx-message-alt-dots font-size-16 align-middle mr-2"></i>
            Left Message
          </a>
          <a
            className="dropdown-item"
            onClick={() => {
              onNewItem({ notesResult: 'Call - No Message', contact: defaultContact, activityType: ContactActivityType.noMessage }).subscribe();
              toggleMenu(false);
            }}
          >
            <i className="bx bx-message-alt font-size-16 align-middle mr-2"></i>
            No Message
          </a>
        </DropdownMenu>
      </Dropdown>
      <button
        type="button"
        className={`btn btn-primary waves-effect waves-light mx-1 mt-2 ${currentView === NotesView.interview ? 'active' : ''}`}
        onClick={() => onChangeView(NotesView.interview)}
      >
        <i className="bx bx-timer font-size-16 align-middle mr-2"></i>
        Interview
      </button>
      {accountSettings.emailSync && (
        <Fragment>
          {!hideAppointments && (
            <button
              type="button"
              className="btn btn-primary waves-effect waves-light mx-1 mt-2"
              onClick={() => {
                openAddCalendarOverlay({
                  selectedEmails: props.defaultEmail ? [props.defaultEmail] : [],
                  availableEmails: props.availableEmails,
                  onClose: props.onReload,
                  meta: props.meta,
                });
              }}
            >
              <i className="bx bx-calendar-plus font-size-16 align-middle mr-2"></i>
              Appointment
            </button>
          )}
          <button type="button" className="btn btn-primary waves-effect waves-light mx-1 mt-2" onClick={() => onChangeView(NotesView.sendEmail)}>
            <i className="bx bx-envelope font-size-16 align-middle mr-2"></i>
            Send Email
          </button>
        </Fragment>
      )}
      <button
        type="button"
        className={`btn btn-primary waves-effect waves-light mx-1 mt-2 ${currentView === NotesView.note ? 'active' : ''}`}
        onClick={() => onChangeView(NotesView.note)}
      >
        <i className="bx bx-notepad font-size-16 align-middle mr-2"></i>
        Note
      </button>
      <button type="button" className="btn btn-primary waves-effect waves-light mx-1 mt-2" onClick={() => onChangeView(NotesView.task)}>
        <i className="bx bx-task font-size-16 align-middle mr-2"></i>
        Task
      </button>
      <button
        type="button"
        className="btn btn-primary waves-effect waves-light mx-1 mt-2"
        onClick={() => openAddTicketModal({ orgId: organization?.id, orgTitle: organization?.name })}
      >
        <i className="bx bx-support font-size-16 align-middle mr-2"></i>
        Ticket
      </button>

      {linkProposal && props.orgId && accountSettings.proposals && (
        <Fragment>
          <Dropdown
            isOpen={showProposal}
            toggle={() => toggleProposal(!showProposal)}
            className="btn-group"
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
          >
            <DropdownToggle className="btn btn-primary waves-light waves-effect dropdown-toggle mx-1 mt-2" tag="button">
              <i className="bx bx-dollar-circle mr-2"></i>
              Proposal
              <i className="mdi mdi-chevron-down ml-2" />
            </DropdownToggle>
            <DropdownMenu>
              <a
                className="dropdown-item"
                onClick={() => {
                  toggleProposal(false);
                  if (isOnePageProposalEnabled) {
                    history.push(
                      `/add-edit-proposal?orgId=${props.orgId}&orgName=${encodeURIComponent(organization?.name)}&lastUrl=/organizations/${
                        props.orgId
                      }/proposals`,
                    );
                  } else {
                    openAddProposalModal({
                      orgId: props.orgId,
                      lastUrl: (location.pathname + location.search).substr(1),
                      meta: props.meta,
                      initialSaleRep: salerep,
                      onDone: () => props.onReload!(),
                    });
                  }
                }}
              >
                <i className="bx bx-plus-circle font-size-16 align-middle mr-2"></i>
                Create Proposal
              </a>
              <a
                className="dropdown-item"
                onClick={() => {
                  toggleProposal(false);
                  openSelectProposalModal({ orgId: props.orgId || '', onAdd: onProposalAdd, selected: selectedProposalIds });
                }}
              >
                <i className="bx bx-dollar-circle font-size-16 align-middle mr-2"></i>
                Link Proposal
              </a>
            </DropdownMenu>
          </Dropdown>
        </Fragment>
      )}

      {canAddPresentation && (
        <Fragment>
          <Dropdown
            isOpen={showPresentation}
            toggle={() => togglePresentation(!showPresentation)}
            className="btn-group"
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
          >
            <DropdownToggle className="btn btn-primary waves-light waves-effect dropdown-toggle mx-1 mt-2" tag="button">
              <i className="bx bxs-slideshow mr-2"></i>
              Presentation
              <i className="mdi mdi-chevron-down ml-2" />
            </DropdownToggle>
            <DropdownMenu>
              <a
                className="dropdown-item"
                onClick={() => {
                  togglePresentation(false);
                  openAddPresentationModal({
                    type: props.meta.type as FormTypes,
                    onSelect: onFormSelect,
                    orgId: props.orgId,
                    folderId: '0',
                    moduleId: props.meta.id,
                    moduleType: props.meta.type as FormTypes,
                  });
                }}
              >
                <i className="bx bx-plus-circle font-size-16 align-middle mr-2"></i>
                Add Presentation
              </a>
            </DropdownMenu>
          </Dropdown>
        </Fragment>
      )}

      {currentView === NotesView.task && <NewTask orgId={organization?.id || props.orgId} />}

      <Collapse isOpen={currentView !== NotesView.none && currentView !== NotesView.sendEmail}>
        {currentView !== NotesView.none &&
          currentView !== NotesView.sendEmail &&
          currentView !== NotesView.appointment &&
          currentView !== NotesView.task && (
            <NewNote
              onClose={() => setCurrentView(NotesView.none)}
              type={currentView}
              onAdd={onNewItem}
              contactId={defaultContact}
              contacts={organization ? organization.contacts : []}
            />
          )}
      </Collapse>
      <Collapse isOpen={currentView === NotesView.sendEmail}>
        <SendEmail contacts={organization ? organization.contacts : []} defaultEmail={props.defaultEmail} />
      </Collapse>
    </div>
  );
  if (organization) return content;

  return (
    <Card>
      <CardBody>{content}</CardBody>
    </Card>
  );
});
